// app/components/native-tapioca-starch-manufacturing-process/ProcessSteps.tsx
"use client";

import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Box } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import CallMadeIcon from "@mui/icons-material/CallMade";
import { PROCESS_ICONS } from "./ProcessIcons";

type Step = { title: string; content: string };

const BRAND = "#3d7237";
const LINE_COLOR = "#cfe1c9";
const NODE_SIZE = 56;

export default function ProcessSteps() {
  const t = useTranslations("ManufacturingProcess");
  const locale = useLocale();
  const steps = t.raw("steps") as Step[];

  const productsHref =
    locale === "zh"
      ? "/zh/thailand-premium-tapioca-starch-products"
      : "/thailand-premium-tapioca-starch-products";
  const contactHref = locale === "zh" ? "/zh/contact-us" : "/contact-us";

  return (
    <Box component="section" sx={{ py: { xs: 6, md: 8 }, bgcolor: "#fff" }}>
      <Container maxWidth="lg">
        <Typography
          variant="body1"
          sx={{ mb: { xs: 5, md: 7 }, color: "#444", maxWidth: 820 }}
          data-aos="fade-up"
        >
          {t("intro")}
        </Typography>

        {/* ===== Connected flow (single continuous spine through every step) ===== */}
        <Box sx={{ position: "relative" }}>
          {/* spine — one continuous line behind every node */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              top: NODE_SIZE / 2,
              bottom: NODE_SIZE / 2,
              left: { xs: NODE_SIZE / 2, md: "50%" },
              width: "3px",
              bgcolor: LINE_COLOR,
              transform: { md: "translateX(-1.5px)" },
              zIndex: 0,
            }}
          />

          {steps.map((step, index) => {
            const Icon = PROCESS_ICONS[index] ?? PROCESS_ICONS[0];
            const isLast = index === steps.length - 1;
            const onLeft = index % 2 === 0; // md+ zig-zag side

            const card = (
              <Box
                sx={{
                  bgcolor: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  p: 2.5,
                }}
              >
                <Typography
                  component="h3"
                  variant="h6"
                  sx={{ fontWeight: 700, color: BRAND, mb: 0.5 }}
                >
                  {step.title}
                </Typography>
                <Typography variant="body2" sx={{ color: "#444" }}>
                  {step.content}
                </Typography>
              </Box>
            );

            return (
              <Box
                key={step.title}
                data-aos="fade-up"
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "grid",
                  gridTemplateColumns: { xs: `${NODE_SIZE}px 1fr`, md: `1fr ${NODE_SIZE}px 1fr` },
                  columnGap: { xs: 2.5, md: 4 },
                  pb: isLast ? 0 : { xs: 4, md: 5 },
                }}
              >
                {/* content — mobile: always col 2; desktop: alternates col 1 / col 3 */}
                <Box
                  sx={{
                    gridColumn: { xs: "2", md: onLeft ? "1" : "3" },
                    gridRow: "1",
                  }}
                >
                  {card}
                </Box>

                {/* icon node — sits on the spine */}
                <Box
                  sx={{
                    gridColumn: { xs: "1", md: "2" },
                    gridRow: "1",
                    width: NODE_SIZE,
                    height: NODE_SIZE,
                    borderRadius: "50%",
                    bgcolor: BRAND,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 10px rgba(61,114,55,0.28)",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={26} />
                </Box>

                {/* spacer to keep the grid's unused side balanced on desktop */}
                <Box sx={{ display: { xs: "none", md: "block" }, gridColumn: onLeft ? "3" : "1" }} />
              </Box>
            );
          })}
        </Box>

        <Typography
          variant="body1"
          sx={{ mt: { xs: 5, md: 7 }, mb: 3, color: "#444", fontWeight: 500, maxWidth: 820 }}
          data-aos="fade-up"
        >
          {t("closing")}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }} data-aos="fade-up">
          <Button
            color="primary"
            variant="contained"
            endIcon={<CallMadeIcon />}
            size="large"
            href={productsHref}
          >
            {t("cta.products")}
          </Button>
          <Button
            color="primary"
            variant="outlined"
            endIcon={<CallMadeIcon />}
            size="large"
            href={contactHref}
          >
            {t("cta.quote")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
